'use client';
import React, { ChangeEvent } from 'react';

import { IconTruck } from '@veneer/core';
import classNames from 'classnames';
import currency from 'currency.js';
import { useTranslation } from 'react-i18next';

import { NamespacesI18n } from '@storefront/i18n';
import ModalPrinterComparison from '@storefront/app/v2/pdp-config/shared-components/modals/modal-printer-comparison/modal-printer-comparison';

import styles from './selection-button-visid.module.scss';

export interface SelectionButtonProps {
  isDisabled?: boolean;
  selectedPaperOption?: boolean;
  name: string;
  value: string;
  id: string;
  onChange?: (event: ChangeEvent<HTMLInputElement>) => void;
  checked: boolean;
  inputTestId: string;
  description?: string;
  image?: string;
  popular?: boolean;
  backOrderShipping?: boolean;
  thumbnail?: string;
  title?: string;
  price?: number | currency;
  bullets?: number;
  optional?: boolean;
  onRemove?: () => void;
  outOfStock?: boolean;
  mainOffer?: boolean;
}

export const SelectionButton = ({
  inputTestId,
  onChange,
  name,
  value,
  description,
  checked,
  isDisabled,
  selectedPaperOption,
  id,
  image,
  popular,
  backOrderShipping,
  bullets,
  thumbnail,
  title = '',
  optional,
  onRemove,
  price,
  outOfStock,
  mainOffer = false,
}: SelectionButtonProps) => {
  const hasImage = image || thumbnail;
  const hasBullets = bullets && bullets > 0;
  const { t } = useTranslation(NamespacesI18n.PDP_CONFIG);

  const handleOptionPrice = () => {
    if (checked) {
      return optional
        ? t('pdpConfig.optionPrice.selected')
        : t('pdpConfig.optionPrice.included');
    }

    if (price === 0) {
      return t('pdpConfig.optionPrice.included');
    }

    return t('pdpConfig.optionPrice.price', {
      price: currency(price, {
        pattern: mainOffer ? '!#' : '+!#',
        negativePattern: '-!#',
      }).format(),
    });
  };
  const priceClass = () => {
    if (optional) {
      return classNames(styles['column-container-optional'], 'body-regular');
    }

    if (checked) {
      return classNames(styles['column-container-text'], 'caption-regular');
    }

    return classNames(
      styles['column-container-text'],
      checked ? 'caption-regular' : 'body-regular'
    );
  };
  const renderPrice = () => (
    <div className={styles['column-price-container']}>
      <span
        id="radio-button-price"
        data-testid="radio-button-price"
        className={priceClass()}
      >
        {handleOptionPrice()}
      </span>
    </div>
  );

  return (
    <>
      <div className={styles['radio-start-container']}>
        <label
          className={classNames('label-radio-button', { disabled: isDisabled })}
          data-testid="radio-extented-test"
          aria-label={name}
        >
          <input
            id={id}
            data-image={image}
            onChange={onChange}
            data-testid={inputTestId}
            disabled={isDisabled || outOfStock}
            className={
              isDisabled || outOfStock
                ? styles['radio-disabled']
                : styles['radio']
            }
            type="radio"
            name={name}
            value={value}
            checked={!isDisabled && checked}
            tabIndex={0}
          />
          <div
            className={styles['radio-extended-rectangular-description']}
            data-testid="radio-rectangular-test"
          >
            <div className={styles['flex-container-wrapper']}>
              <div className={styles['badge-container']}>
                {popular && !outOfStock && !backOrderShipping && (
                  <span
                    className={styles['popular-badge-text']}
                    data-testid="popular-badge"
                  >
                    Popular
                  </span>
                )}
                {outOfStock && !backOrderShipping && (
                  <span
                    className={styles['out-of-stock-badge-text']}
                    data-testid="out-of-stock-badge"
                  >
                    {t('pdpConfig.outOfStock.title')}
                  </span>
                )}
                {backOrderShipping && (
                  <span
                    className={styles['back-order-badge-text']}
                    data-testid="back-order-badge"
                  >
                    {t('pdpConfig.backOrder.title')}
                  </span>
                )}
              </div>
              <div
                className={
                  hasImage
                    ? styles['flex-container-content']
                    : styles['flex-container-content-no-image']
                }
              >
                {hasImage && (
                  <div className={styles['flex-container-image']}>
                    <img
                      src={thumbnail || image}
                      className={styles['img']}
                      alt={title}
                    />
                  </div>
                )}
                <div
                  data-testid="radio-button-text-content"
                  className={
                    optional
                      ? styles['column-container-optional']
                      : styles['column-container-text']
                  }
                >
                  <div
                    className={
                      hasImage || hasBullets
                        ? styles['title-row-content']
                        : styles['title-row-content-yes-or-no']
                    }
                  >
                    <div
                      className={classNames(
                        hasImage ? styles['title'] : styles['titleNoImage'],
                        'body-extra-large'
                      )}
                    >
                      {optional ? t('pdpConfig.optional.add') : title}
                    </div>
                    {hasImage && (
                      <>
                        <span className={styles['separator']}>{'|'}</span>
                        <span
                          className={classNames(styles['name'], 'body-large')}
                        >
                          {name}
                        </span>
                      </>
                    )}
                    {hasBullets &&
                      Array.from({ length: bullets }).map((_, index) => (
                        <span
                          key={bullets + `${index}`}
                          className={classNames(styles['bullet'], {
                            [styles['disabled']]: isDisabled,
                          })}
                        />
                      ))}
                    {!hasImage && !hasBullets && renderPrice()}
                  </div>
                  {(hasImage || hasBullets) && (
                    <div className={styles['second-row-content']}>
                      <div
                        id="radio-button-content"
                        className={styles['radio-button-content']}
                      >
                        <span
                          className={classNames('caption-regular')}
                          dangerouslySetInnerHTML={{
                            __html: description || '',
                          }}
                        />
                      </div>
                      {renderPrice()}
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </label>
        <div className={styles['informative-label-container']}>
          {outOfStock && (
            <div data-testid="out-of-stock-label">
              <div className={styles['background-overlay']} />
              <div className={styles['label']}>
                <p>{t('pdpConfig.outOfStock.label1')}</p>
                <ModalPrinterComparison
                  noFirstText={true}
                  secondText="options"
                  underlineLink={true}
                />
                <p>{t('pdpConfig.outOfStock.label2')}</p>
              </div>
            </div>
          )}
        </div>
        <div className={styles['informative-label-container']}>
          {backOrderShipping && (
            <div data-testid="back-order-label">
              <div className={styles['background-overlay']} />
              <div
                className={classNames(
                  styles['label'],
                  styles['centered-label']
                )}
              >
                <IconTruck
                  className={styles['icon-checkmark']}
                  size={24}
                  id="checkmark-icon"
                  color={'gray12'}
                />
                <p
                  dangerouslySetInnerHTML={{
                    __html: t('pdpConfig.backOrder.label'),
                  }}
                />
              </div>
            </div>
          )}
        </div>
      </div>
      {optional && (
        <div className={styles['radio-start-container']}>
          <label
            className={classNames('label-radio-button', {
              disabled: isDisabled,
            })}
            data-testid="radio-extented-test"
            aria-label={optional ? t('pdpConfig.optional.remove') : name}
          >
            <input
              id={`${id}-remove`}
              data-testid={`${inputTestId}-remove`}
              disabled={isDisabled}
              className={
                isDisabled ? styles['radio-disabled'] : styles['radio']
              }
              type="radio"
              name={name}
              onChange={onRemove}
              value={value}
              checked={!isDisabled && !checked && selectedPaperOption}
              tabIndex={0}
            />
            <div
              className={styles['radio-extended-rectangular-description']}
              data-testid="radio-rectangular-test"
            >
              <div className={styles['flex-container-wrapper']}>
                <div className={styles['flex-container-content-no-image']}>
                  <div className={styles['column-container-text']}>
                    <div className={styles['title-row-content']}>
                      <span
                        className={classNames(
                          styles['titleNoImage'],
                          'body-extra-large'
                        )}
                      >
                        {t('pdpConfig.optional.remove')}
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </label>
        </div>
      )}
    </>
  );
};
